import { Service, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthenticationService } from '../authentication/authentication.service';
import { hasRole, Role } from '../shared/enums/role';
import { DialogService } from '../shared/dialogs/dialog.service';
import {
  ActionQuoteDto,
  ChargeQuoteResultDto,
  CreditQuoteContextDto,
  MceApiService,
} from './mceapi.client.generated';

export enum CreditAction {
  CableAdd = 'cable.add',
  CableCopy = 'cable.copy',
  CableCopyMultiple = 'cable.copy-multiple',
  ProjectAdd = 'project.add',
  ProjectCopy = 'project.copy',
  ProjectAddUser = 'project.add-user',
  ReportsCablesList = 'report.cables-list',
  ReportCableScheduleV1 = 'report.cable-schedule-v1',
  ReportCableScheduleV2 = 'report.cable-schedule-v2',
  ReportCableComments = 'report.cable-comments',
  ReportCableSizing = 'report.cable-sizing',
  ReportPowerLoss = 'report.power-loss',
  ReportProjectSummary = 'report.project-summary',
  ReportProjectCableSizing = 'report.project-cable-sizing',
  ReportLoadList = 'report.load-list',
  ReportMTOCables = 'report.mto-cables',
  ReportVoltageDropMatrix = 'report.voltage-drop-matrix',
  ExportData = 'export.data',
}

export interface BillingQuoteRequest {
  action: CreditAction;
  cableCount?: number;
}

@Service()
export class BillingChargeService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly dialogService = inject(DialogService);
  private readonly mceApiService = inject(MceApiService);

  getQuote(request: BillingQuoteRequest) {
    return this.mceApiService.v1BillingQuote(this.toQuoteContext(request));
  }

  async ensureCanProceed(chargeQuote: ChargeQuoteResultDto | null | undefined): Promise<boolean> {
    const quote = chargeQuote?.quote;

    if (this.hasSufficientCredits(quote)) {
      return true;
    }

    const { title, message } = this.getInsufficientCreditsDialogContent();
    await firstValueFrom(this.dialogService.acknowledge(title, message, '320px'));
    return false;
  }

  applyCharge(charge: string | Pick<ChargeQuoteResultDto, 'token'>) {
    return this.mceApiService.v1BillingCharge({
      token: this.getToken(charge),
    });
  }

  private toQuoteContext(request: BillingQuoteRequest): CreditQuoteContextDto {
    return {
      key: request.action,
      cableCount: request.cableCount,
    };
  }

  private getToken(charge: string | Pick<ChargeQuoteResultDto, 'token'>): string | undefined {
    return typeof charge === 'string' ? charge : charge.token;
  }

  private hasSufficientCredits(quote: ActionQuoteDto | undefined): boolean {
    return quote?.hasSufficientCredits === true;
  }

  private getInsufficientCreditsDialogContent(): { title: string; message: string } {
    const roles = this.authenticationService.currentUserRoles();
    if (hasRole(roles, Role.SharedBillingMember)) {
      return {
        title: 'Insufficient shared billing budget',
        message: "This action cannot be completed because the shared billing owner's account does not have enough remaining credits or PAYG budget. Please contact the shared billing owner and ask them to increase their PAYG budget.",
      };
    }

    const isProTier = hasRole(roles, Role.ProTier);

    if (isProTier) {
      return {
        title: 'Insufficient PAYG budget',
        message: 'This action cannot be completed because your account does not have enough remaining credits or PAYG budget. Please increase your PAYG budget.',
      };
    }

    return {
      title: 'Insufficient credits',
      message: 'This action cannot be completed because your Free plan does not have enough remaining credits. Please subscribe to Pro and try again.',
    };
  }
}
